/**
 * Sister registry to {@link ./chart-registry.ts} for compare-table
 * widgets. Bar/line/pie are ECharts canvases the PDF exporter
 * snapshots via getInstance; compare-tables are pure DOM with no
 * canvas, so PDF needs the resolved winner / loser delta rows
 * instead and re-renders them as jsPDF text tables on their own
 * page.
 *
 * Storage is delegated to {@link createRegistry} (R-18); only the
 * entry shape and the React hook stay local to this module.
 */
import { useEffect } from 'react';
import type { CompareDeltaRow } from '@/engine/pivots/compare';
import { createRegistry } from './create-registry';

export type CompareTableSnapshot = {
  /** Top-N positive movers, in order. */
  winners: CompareDeltaRow[];
  /** Top-N negative movers, sorted asc by delta. */
  losers: CompareDeltaRow[];
  /** Period A label rendered as the second column header. */
  labelA: string;
  /** Period B label rendered as the third column header. */
  labelB: string;
  /** Top-N limit, surfaced in the section headings on the PDF page. */
  limit: number;
};

export type CompareTableGetSnapshot = () => CompareTableSnapshot | null;

export type CompareTableRegistryEntry = {
  id: string;
  title: string;
  getSnapshot: CompareTableGetSnapshot;
};

const registry = createRegistry<CompareTableRegistryEntry>();

/** Register a compare-table widget. Returns an unregister function. */
export function registerCompareTable(
  entry: CompareTableRegistryEntry,
): () => void {
  return registry.register(entry);
}

/** Snapshot the registry as a stable list (insertion order). */
export function listCompareTables(): CompareTableRegistryEntry[] {
  return registry.list();
}

/** Visible for testing. */
export function _resetCompareTableRegistryForTests(): void {
  registry.reset();
}

/**
 * React hook that keeps a compare-table widget registered for as
 * long as the component is mounted. `getSnapshot` is expected to be
 * a stable callback (typically built with `useCallback`); without
 * that, the effect's dep array would re-register on every render.
 */
export function useCompareTableRegistration(
  id: string,
  title: string,
  getSnapshot: CompareTableGetSnapshot,
): void {
  useEffect(() => {
    return registerCompareTable({ id, title, getSnapshot });
  }, [id, title, getSnapshot]);
}
